import React, { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import config from "../../../../config";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Code from "@editorjs/code";
import Embed from "@editorjs/embed";
import Table from "@editorjs/table";
import Marker from "@editorjs/marker";
import InlineCode from "@editorjs/inline-code";
import Image from "@editorjs/image";

// Block Tune that adds an "Alt text" field to the image block's settings
// menu (the gear icon), separate from the visible caption — alt text is for
// accessibility/SEO and isn't meant to be displayed on the page.
class AltTextTune {
  static get isTune() {
    return true;
  }

  constructor({ data }) {
    this.data = data && typeof data === "object" ? data : { alt: "" };
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.style.padding = "8px 12px";

    const label = document.createElement("div");
    label.textContent = "Alt text (for accessibility & SEO)";
    label.style.fontSize = "12px";
    label.style.marginBottom = "4px";
    label.style.color = "#707684";

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Describe this image...";
    input.value = this.data.alt || "";
    input.style.width = "100%";
    input.style.boxSizing = "border-box";
    input.style.border = "1px solid #e8e8eb";
    input.style.borderRadius = "4px";
    input.style.padding = "4px 8px";
    input.addEventListener("input", (e) => {
      this.data.alt = e.target.value;
    });
    input.addEventListener("click", (e) => e.stopPropagation());

    wrapper.appendChild(label);
    wrapper.appendChild(input);
    return wrapper;
  }

  save() {
    return this.data;
  }
}

// Escapes plain text before it's dropped into an HTML template — the
// Key Takeaways bullets are deliberately plain text (no inline formatting),
// unlike paragraph/list blocks elsewhere which intentionally store HTML.
const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Marker attribute used to recognize a saved Key Takeaways block again when
// re-opening a post for edit (see the "div"/"section"/"article" case in
// convertToBlocks below) — without it the div would just get unwrapped like
// any other container and the block would lose its identity on reload.
const KEY_TAKEAWAYS_MARKER = "keyTakeaways";

// Custom EditorJS block: a fixed "Key Takeaways" heading plus a bullet list
// the editor can freely add/remove/edit — no rich text, just plain lines —
// so it can be dropped anywhere in the article flow like any other block.
class KeyTakeawaysTool {
  static get toolbox() {
    return {
      title: "Key Takeaways",
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2 14.5 8.5 21 9.5 16.2 14 17.5 21 12 17.5 6.5 21 7.8 14 3 9.5 9.5 8.5 12 2Z"/></svg>',
    };
  }

  constructor({ data }) {
    const items =
      data && Array.isArray(data.items) ? data.items.filter((i) => i !== null && i !== undefined) : [];
    this.data = { items: items.length > 0 ? items : [""] };
    this.wrapper = null;
    this.list = null;
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.style.cssText =
      "border:2px solid #f6c9d8;background:#fff0f5;border-radius:16px;padding:20px 24px;margin:8px 0;";

    const heading = document.createElement("h4");
    heading.textContent = "Key Takeaways";
    heading.contentEditable = false;
    heading.style.cssText =
      "margin:0 0 12px 0;font-size:20px;font-weight:700;color:#1f2937;";

    this.list = document.createElement("div");
    this.renderItems();

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.textContent = "+ Add point";
    addBtn.style.cssText =
      "margin-top:4px;padding:6px 12px;border:1px solid #ec4899;background:#ffffff;color:#ec4899;border-radius:6px;cursor:pointer;font-size:13px;";
    addBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.data.items.push("");
      this.renderItems();
      const inputs = this.list.querySelectorAll("[contenteditable]");
      inputs[inputs.length - 1]?.focus();
    });

    this.wrapper.appendChild(heading);
    this.wrapper.appendChild(this.list);
    this.wrapper.appendChild(addBtn);

    return this.wrapper;
  }

  renderItems() {
    this.list.innerHTML = "";
    this.data.items.forEach((item, index) => {
      const row = document.createElement("div");
      row.style.cssText =
        "display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;";

      const bullet = document.createElement("span");
      bullet.textContent = "•";
      bullet.style.cssText =
        "color:#ec4899;font-size:18px;line-height:1.6;padding-top:1px;";

      const input = document.createElement("div");
      input.contentEditable = true;
      input.textContent = item || "";
      input.style.cssText =
        "flex:1;outline:none;font-size:15px;line-height:1.6;color:#1f2937;min-height:1.4em;";

      input.addEventListener("input", () => {
        this.data.items[index] = input.textContent;
      });

      input.addEventListener("paste", (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData(
          "text/plain"
        );
        document.execCommand("insertText", false, text);
      });

      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.data.items.splice(index + 1, 0, "");
          this.renderItems();
          const inputs = this.list.querySelectorAll("[contenteditable]");
          inputs[index + 1]?.focus();
        } else if (
          e.key === "Backspace" &&
          input.textContent === "" &&
          this.data.items.length > 1
        ) {
          e.preventDefault();
          this.data.items.splice(index, 1);
          this.renderItems();
          const inputs = this.list.querySelectorAll("[contenteditable]");
          inputs[Math.max(0, index - 1)]?.focus();
        }
      });

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.innerHTML = "&times;";
      removeBtn.title = "Remove";
      removeBtn.style.cssText =
        "border:none;background:transparent;color:#9ca3af;cursor:pointer;font-size:16px;line-height:1;padding:2px 4px;";
      removeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        if (this.data.items.length <= 1) {
          this.data.items[0] = "";
          this.renderItems();
          return;
        }
        this.data.items.splice(index, 1);
        this.renderItems();
      });

      row.appendChild(bullet);
      row.appendChild(input);
      row.appendChild(removeBtn);
      this.list.appendChild(row);
    });
  }

  save() {
    return {
      items: this.data.items.map((t) => (t || "").trim()).filter(Boolean),
    };
  }

  validate(savedData) {
    return Array.isArray(savedData.items) && savedData.items.length > 0;
  }
}

// Raw inline SVGs (lucide-react "shield-check", "indian-rupee",
// "message-circle-question", "eye-off", "building-2", "headset" path data) —
// used as literal strings because the final blog HTML runs through
// dangerouslySetInnerHTML on the public site, which can't render React icon
// components.
const ICON_SVGS = {
  shieldCheck:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
  indianRupee:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12"/><path d="M6 8h12"/><path d="m6 13 8.5 8"/><path d="M6 13h3"/><path d="M9 13c6.667 0 6.667-10 0-10"/></svg>',
  messageCircleQuestion:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>',
  eyeOff:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>',
  building2:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>',
  headset:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z"/><path d="M21 16v2a4 4 0 0 1-4 4h-5"/></svg>',
};

// Same URL as DEFAULT_VENUE_CTA_IMAGE in the website repo's
// components/mainComponents/reusableSections/VenueCTACard.tsx — kept in sync
// so the "placeholder" shown here matches what the public site falls back to
// when no image was uploaded for this block.
const DEFAULT_VENUE_CTA_IMAGE =
  "https://bookwedgo-prod.s3.us-east-1.amazonaws.com/uploads/25df094fbc1348d7b5806e37a4f3b2e1_Bharani-Function-Hall-in-Saligramam-Banner-web-01.jpg";

const VENUE_CTA_MARKER = "venueCta";

// Heading/pill copy is fixed for now (only the image and the two button
// links are editable) — kept in the data shape anyway so making them
// editable later doesn't require a data migration.
const VENUE_CTA_DEFAULTS = {
  heading: "Find the Perfect Wedding Venue That Matches Your Budget",
  primaryButton: { label: "Find Your Perfect Venue", href: "/wedding" },
  secondaryButton: { label: "Talk to a Venue Expert", href: "/contact" },
};

const VENUE_CTA_FEATURES = [
  { label: "Verified Venues", icon: "shieldCheck", color: "#e11d48" },
  { label: "Best price Assistance", icon: "indianRupee", color: "#f97316" },
  { label: "Instant Enquiry", icon: "messageCircleQuestion", color: "#e11d48" },
  { label: "No Hidden Charges", icon: "eyeOff", color: "#6b7280" },
];

// Custom EditorJS block: the "Find the Perfect Wedding Venue" promo card.
// Image is upload-or-placeholder, heading/pill text are fixed for now, and
// the two button destinations are editable so admins can point them at
// different venue/contact pages per post.
class VenueCTATool {
  static get toolbox() {
    return {
      title: "Venue CTA",
      icon: ICON_SVGS.building2,
    };
  }

  constructor({ data }) {
    this.data = {
      image: data?.image?.url ? { url: data.image.url } : null,
      heading: data?.heading || VENUE_CTA_DEFAULTS.heading,
      primaryButton: {
        label: VENUE_CTA_DEFAULTS.primaryButton.label,
        href: data?.primaryButton?.href || VENUE_CTA_DEFAULTS.primaryButton.href,
      },
      secondaryButton: {
        label: VENUE_CTA_DEFAULTS.secondaryButton.label,
        href: data?.secondaryButton?.href || VENUE_CTA_DEFAULTS.secondaryButton.href,
      },
    };
    this.wrapper = null;
    this.imageBox = null;
    this.fileInput = null;
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.style.cssText =
      "border-radius:24px;background:linear-gradient(135deg,#fff7ed,#fff1f2,#fce7f3);padding:20px;margin:8px 0;";

    const layout = document.createElement("div");
    layout.style.cssText = "display:flex;flex-wrap:wrap;gap:20px;align-items:stretch;";

    this.imageBox = document.createElement("div");
    this.imageBox.style.cssText =
      "position:relative;flex:1 1 240px;min-height:220px;border-radius:16px;overflow:hidden;cursor:pointer;background-size:cover;background-position:center;";
    this.imageBox.title = "Click to upload an image";
    this.imageBox.addEventListener("click", () => this.fileInput.click());
    this.renderImage();

    this.fileInput = document.createElement("input");
    this.fileInput.type = "file";
    this.fileInput.accept = "image/*";
    this.fileInput.style.display = "none";
    this.fileInput.addEventListener("change", (e) => this.handleImageUpload(e));

    const content = document.createElement("div");
    content.style.cssText = "flex:2 1 320px;display:flex;flex-direction:column;justify-content:center;";

    const heading = document.createElement("h4");
    heading.textContent = this.data.heading;
    heading.contentEditable = false;
    heading.style.cssText =
      "margin:0 0 14px 0;font-size:22px;font-weight:800;color:#1f2937;line-height:1.25;";

    const featuresGrid = document.createElement("div");
    featuresGrid.style.cssText =
      "display:grid;grid-template-columns:repeat(2,minmax(140px,1fr));gap:10px;margin-bottom:16px;";
    VENUE_CTA_FEATURES.forEach((feature) => {
      const pill = document.createElement("div");
      pill.style.cssText =
        "display:flex;align-items:center;gap:10px;background:rgba(255,255,255,.6);border-radius:16px;padding:10px 14px;";

      const iconWrap = document.createElement("span");
      iconWrap.style.cssText = `display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:9999px;background:#fff;color:${feature.color};box-shadow:0 1px 2px rgba(0,0,0,.08);flex-shrink:0;`;
      iconWrap.innerHTML = ICON_SVGS[feature.icon];

      const label = document.createElement("span");
      label.textContent = feature.label;
      label.style.cssText = "color:#374151;font-weight:500;font-size:14px;";

      pill.appendChild(iconWrap);
      pill.appendChild(label);
      featuresGrid.appendChild(pill);
    });

    const buttonsRow = document.createElement("div");
    buttonsRow.style.cssText = "display:flex;flex-wrap:wrap;gap:12px;";
    buttonsRow.appendChild(
      this.renderButtonEditor(
        this.data.primaryButton,
        "2px solid #fb923c",
        "rgba(255,255,255,.7)",
        "#1f2937",
        ICON_SVGS.building2,
        "#f97316",
        "#fff",
        (href) => (this.data.primaryButton.href = href)
      )
    );
    buttonsRow.appendChild(
      this.renderButtonEditor(
        this.data.secondaryButton,
        "none",
        "linear-gradient(90deg,#db2777,#e11d48)",
        "#fff",
        ICON_SVGS.headset,
        "rgba(255,255,255,.25)",
        "#fff",
        (href) => (this.data.secondaryButton.href = href)
      )
    );

    content.appendChild(heading);
    content.appendChild(featuresGrid);
    content.appendChild(buttonsRow);

    layout.appendChild(this.imageBox);
    layout.appendChild(content);

    this.wrapper.appendChild(layout);
    this.wrapper.appendChild(this.fileInput);

    return this.wrapper;
  }

  renderImage() {
    const url = this.data.image?.url || DEFAULT_VENUE_CTA_IMAGE;
    this.imageBox.style.backgroundImage = `url("${url}")`;
    this.imageBox.innerHTML = this.data.image?.url
      ? `<div style="position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,.55);color:#fff;font-size:11px;padding:4px 8px;border-radius:6px;">Click to change image</div>`
      : `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.35);color:#fff;font-size:13px;font-weight:600;text-align:center;padding:12px;">Placeholder image — click to upload your own</div>`;
  }

  async handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    this.imageBox.style.opacity = "0.5";
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await axios.post(
        `${config.service}/masters/blogpost/upload-image/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      this.data.image = { url: res.data.url };
      this.renderImage();
    } catch (err) {
      console.error("Venue CTA image upload failed:", err);
    } finally {
      this.imageBox.style.opacity = "1";
    }
  }

  renderButtonEditor(buttonData, border, background, color, iconSvg, iconBg, iconColor, onHrefChange) {
    const group = document.createElement("div");
    group.style.cssText = "display:flex;flex-direction:column;gap:4px;";

    const btn = document.createElement("div");
    btn.style.cssText = `display:inline-flex;align-items:center;gap:8px;border-radius:9999px;padding:10px 18px;font-weight:600;font-size:14px;border:${border};background:${background};color:${color};width:fit-content;`;

    const iconWrap = document.createElement("span");
    iconWrap.style.cssText = `display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:8px;background:${iconBg};color:${iconColor};flex-shrink:0;`;
    iconWrap.innerHTML = iconSvg;

    const label = document.createElement("span");
    label.textContent = buttonData.label;

    btn.appendChild(iconWrap);
    btn.appendChild(label);

    const linkInput = document.createElement("input");
    linkInput.type = "text";
    linkInput.value = buttonData.href;
    linkInput.placeholder = "Button link, e.g. /wedding";
    linkInput.style.cssText =
      "font-size:12px;border:1px solid #e5e7eb;border-radius:6px;padding:4px 8px;color:#374151;background:#fff;";
    linkInput.addEventListener("click", (e) => e.stopPropagation());
    linkInput.addEventListener("input", (e) => onHrefChange(e.target.value));

    group.appendChild(btn);
    group.appendChild(linkInput);
    return group;
  }

  save() {
    return {
      image: this.data.image,
      heading: this.data.heading,
      primaryButton: { ...this.data.primaryButton },
      secondaryButton: { ...this.data.secondaryButton },
    };
  }

  validate() {
    return true;
  }
}

const EditorJsComponent = forwardRef(({
  data,
  onChange,
  name,
  placeholder = "Start writing your content...",
}, ref) => {
  const editorInstance = useRef(null);
  const editorContainerRef = useRef(null);
  const wrapperRef = useRef(null);
  const isInitialized = useRef(false);

  // Converts HTML or plain text to Editor.js blocks
  const convertToBlocks = useCallback((content) => {
    if (!content) return { time: Date.now(), blocks: [], version: "2.26.0" };

    // Already Editor.js JSON
    try {
      const parsed =
        typeof content === "string" ? JSON.parse(content) : content;
      if (parsed.blocks && Array.isArray(parsed.blocks)) return parsed;
    } catch (e) {}

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const blocks = [];

    const blockLevelTags = [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "blockquote",
      "pre",
      "ul",
      "ol",
      "table",
      "figure",
      "div",
      "section",
      "article",
    ];

    const isBlockLevel = (tag) => blockLevelTags.includes(tag.toLowerCase());

    let currentText = "";

    function traverse(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        currentText += node.textContent;
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const tag = node.tagName.toLowerCase();

      if (isBlockLevel(tag)) {
        // Flush pending text as paragraph
        if (currentText.trim()) {
          blocks.push({
            type: "paragraph",
            data: { text: currentText.trim() },
          });
          currentText = "";
        }

        let block = null;
        switch (tag) {
          case "h1":
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            const level = parseInt(tag[1]);
            const headerHtml = node.innerHTML.trim();
            if (headerHtml) {
              block = { type: "header", data: { text: headerHtml, level } };
            }
            break;
          case "p":
            const paraHtml = node.innerHTML.trim();
            if (paraHtml) {
              block = { type: "paragraph", data: { text: paraHtml } };
            }
            break;
          case "blockquote":
            const quoteHtml = node.innerHTML.trim();
            if (quoteHtml) {
              block = {
                type: "quote",
                data: { text: quoteHtml, alignment: "left" },
              };
            }
            break;
          case "pre":
            const codeText = node.textContent.trim();
            if (codeText) {
              block = { type: "code", data: { code: codeText } };
            }
            break;
          case "ul":
          case "ol":
            const style = tag === "ul" ? "unordered" : "ordered";
            const items = Array.from(node.querySelectorAll("li"))
              .map((li) => li.innerHTML.trim())
              .filter(Boolean);
            if (items.length > 0) {
              block = { type: "list", data: { style, items } };
            }
            break;
          case "table":
            const content = Array.from(node.querySelectorAll("tr"))
              .map((tr) =>
                Array.from(tr.querySelectorAll("td, th")).map((cell) =>
                  cell.innerHTML.trim()
                )
              )
              .filter((row) => row.some((cell) => cell));
            if (content.length > 0) {
              block = { type: "table", data: { content } };
            }
            break;
          case "figure":
            const img = node.querySelector("img");
            const captionEl = node.querySelector("figcaption");
            if (img?.src) {
              block = {
                type: "image",
                data: {
                  file: { url: img.src },
                  caption: captionEl?.textContent?.trim() || "",
                  withBorder: false,
                  withBackground: false,
                  stretched: false,
                },
                tunes: {
                  altText: { alt: img.getAttribute("alt") || "" },
                },
              };
            }
            break;
          case "div":
            if (node.getAttribute("data-editorjs-type") === KEY_TAKEAWAYS_MARKER) {
              const takeawayItems = Array.from(node.querySelectorAll("li"))
                .map((li) => li.textContent.trim())
                .filter(Boolean);
              if (takeawayItems.length > 0) {
                block = { type: "keyTakeaways", data: { items: takeawayItems } };
              }
              break;
            }
            if (node.getAttribute("data-editorjs-type") === VENUE_CTA_MARKER) {
              const img = node.querySelector("img");
              const anchors = node.querySelectorAll("a");
              const headingEl = node.querySelector("h3");
              block = {
                type: "venueCta",
                data: {
                  image: img?.getAttribute("src")
                    ? { url: img.getAttribute("src") }
                    : null,
                  heading:
                    headingEl?.textContent?.trim() || VENUE_CTA_DEFAULTS.heading,
                  primaryButton: {
                    label: VENUE_CTA_DEFAULTS.primaryButton.label,
                    href:
                      node.getAttribute("data-primary-href") ||
                      anchors[0]?.getAttribute("href") ||
                      VENUE_CTA_DEFAULTS.primaryButton.href,
                  },
                  secondaryButton: {
                    label: VENUE_CTA_DEFAULTS.secondaryButton.label,
                    href:
                      node.getAttribute("data-secondary-href") ||
                      anchors[1]?.getAttribute("href") ||
                      VENUE_CTA_DEFAULTS.secondaryButton.href,
                  },
                },
              };
              break;
            }
          // eslint-disable-next-line no-fallthrough
          case "section":
          case "article":
            node.childNodes.forEach(traverse);
            if (currentText.trim()) {
              blocks.push({
                type: "paragraph",
                data: { text: currentText.trim() },
              });
              currentText = "";
            }
            return;
          default:
            const defaultHtml = node.innerHTML.trim();
            if (defaultHtml) {
              block = { type: "paragraph", data: { text: defaultHtml } };
            }
        }

        if (block) blocks.push(block);
      } else {
        node.childNodes.forEach(traverse);
      }
    }

    tempDiv.childNodes.forEach(traverse);

    // Final flush
    if (currentText.trim()) {
      blocks.push({
        type: "paragraph",
        data: { text: currentText.trim() },
      });
    }

    if (blocks.length === 0 && content.toString().trim()) {
      const plainText = content
        .toString()
        .replace(/<[^>]*>/g, "")
        .trim();
      if (plainText) {
        blocks.push({
          type: "paragraph",
          data: { text: plainText },
        });
      }
    }

    return { time: Date.now(), blocks, version: "2.26.0" };
  }, []);

  const blocksToHtml = useCallback((blocks) => {
    if (!blocks || !Array.isArray(blocks)) return "";
    console.log("Converting blocks to HTML:", blocks);

    return blocks
      .map((block) => {
        // Guard against malformed blocks
        if (!block || !block.type || !block.data) {
          console.warn("Malformed block:", block);
          return "";
        }

        const text = block.data.text || "";

        switch (block.type) {
          case "paragraph":
            return `<p class="text-base leading-relaxed text-gray-900 dark:text-gray-100">${text}</p>`;

          case "header":
            const level = block.data.level || 2;
            const headerClasses = `text-gray-900 dark:text-gray-100 font-bold leading-tight ${
              level === 1
                ? "text-4xl"
                : level === 2
                ? "text-3xl"
                : level === 3
                ? "text-2xl"
                : level === 4
                ? "text-xl"
                : level === 5
                ? "text-lg"
                : "text-base"
            }`;
            return `<h${level} class="${headerClasses}">${text}</h${level}>`;

          case "quote":
            return `<blockquote class="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300">${text}</blockquote>`;

          case "list": {
            const tag = block.data.style === "ordered" ? "ol" : "ul";
            const listClasses =
              block.data.style === "ordered"
                ? "list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-0"
                : "list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-0";

            // Debug log to see what we're working with
            console.log("List items:", block.data.items);

            // Ensure items is an array
            const items = Array.isArray(block.data.items)
              ? block.data.items
                  .map((item) => {
                    console.log("Processing item:", item, "Type:", typeof item);

                    let itemText = "";

                    // Handle different item formats
                    if (typeof item === "string") {
                      itemText = item;
                    } else if (item && typeof item === "object") {
                      // Explicitly access the content property
                      itemText = String(item.content || item.text || "");
                    } else {
                      itemText = "";
                    }

                    // Trim whitespace and newlines
                    itemText = itemText.trim();

                    console.log("Final itemText:", itemText);

                    return itemText
                      ? `<li class="text-base leading-relaxed">${itemText}</li>`
                      : "";
                  })
                  .filter((li) => li !== "")
                  .join("")
              : "";

            return items
              ? `<${tag} class="${listClasses}">${items}</${tag}>`
              : "";
          }

          case "code":
            return `<pre class="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto"><code class="text-sm font-mono text-gray-800 dark:text-gray-200">${
              block.data.code || ""
            }</code></pre>`;

          case "table": {
            // Ensure content is an array
            if (!Array.isArray(block.data.content)) return "";

            const rows = block.data.content
              .map((row) => {
                if (!Array.isArray(row)) return "";
                return `<tr class="border-b border-gray-200 dark:border-gray-700">${row
                  .map(
                    (cell) =>
                      `<td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">${
                        cell || ""
                      }</td>`
                  )
                  .join("")}</tr>`;
              })
              .join("");
            return `<table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm"><tbody>${rows}</tbody></table>`;
          }

          case "image": {
            const url = block.data.file?.url || block.data.url;
            const caption = block.data.caption || "";
            const altText = block.tunes?.altText?.alt || caption || "";

            if (url) {
              return `<figure class="my-4 text-center"><img src="${url}" alt="${altText}" class="mx-auto max-w-full h-auto rounded-lg shadow-md">${
                caption
                  ? `<figcaption class="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">${caption}</figcaption>`
                  : ""
              }</figure>`;
            }
            return "";
          }

          case "keyTakeaways": {
            const items = Array.isArray(block.data.items)
              ? block.data.items.filter((i) => typeof i === "string" && i.trim())
              : [];
            if (items.length === 0) return "";

            const itemsHtml = items
              .map(
                (item) =>
                  `<li style="margin-bottom:12px;">${escapeHtml(item.trim())}</li>`
              )
              .join("");

            return `<div data-editorjs-type="${KEY_TAKEAWAYS_MARKER}" style="border:2px solid #f6c9d8;background:#fff0f5;border-radius:16px;padding:24px 28px;margin:24px 0;"><h4 style="margin:0 0 16px 0;font-size:22px;font-weight:700;color:#1f2937;">Key Takeaways</h4><ul style="list-style:disc;padding-left:20px;margin:0;color:#1f2937;font-size:16px;line-height:1.6;">${itemsHtml}</ul></div>`;
          }

          case "venueCta": {
            const image = block.data.image?.url || DEFAULT_VENUE_CTA_IMAGE;
            const heading = escapeHtml(block.data.heading || VENUE_CTA_DEFAULTS.heading);
            const primaryHref = escapeHtml(
              block.data.primaryButton?.href || VENUE_CTA_DEFAULTS.primaryButton.href
            );
            const secondaryHref = escapeHtml(
              block.data.secondaryButton?.href || VENUE_CTA_DEFAULTS.secondaryButton.href
            );
            const primaryLabel = escapeHtml(VENUE_CTA_DEFAULTS.primaryButton.label);
            const secondaryLabel = escapeHtml(VENUE_CTA_DEFAULTS.secondaryButton.label);

            // Same Tailwind classes as the website's VenueCTACard.tsx, so
            // this HTML renders styled on the public site without relying on
            // arbitrary utility classes Tailwind's JIT scan might purge.
            const featuresHtml = VENUE_CTA_FEATURES.map(
              (feature) =>
                `<div class="flex items-center gap-3 bg-white/60 rounded-2xl px-4 py-3"><span class="flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm shrink-0" style="color:${feature.color}">${ICON_SVGS[feature.icon]}</span><span class="text-gray-800 font-medium text-sm sm:text-base">${escapeHtml(feature.label)}</span></div>`
            ).join("");

            // The blog content on the public site is wrapped in a Tailwind
            // Typography "prose" container, which forces default margins on
            // h3/img and forces all links blue+underlined. Inline styles on
            // those three tags override that regardless of the site's CSS,
            // since prose's rules aren't !important.
            return `<div data-editorjs-type="${VENUE_CTA_MARKER}" data-primary-href="${primaryHref}" data-secondary-href="${secondaryHref}" class="rounded-3xl bg-gradient-to-br from-orange-50 via-rose-50 to-pink-100 p-5 sm:p-8 my-8"><div class="flex flex-col md:flex-row gap-6 md:gap-8 items-stretch"><div class="relative w-full md:w-[38%] min-h-[220px] md:min-h-[260px] rounded-2xl overflow-hidden shadow-md shrink-0"><img src="${image}" alt="${heading}" class="absolute inset-0 w-full h-full object-cover" style="margin:0;" /></div><div class="flex-1 flex flex-col justify-center min-w-0"><h3 class="text-2xl sm:text-3xl md:text-[2rem] font-extrabold text-gray-900 leading-tight mb-5" style="margin-top:0;">${heading}</h3><div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">${featuresHtml}</div><div class="flex flex-col sm:flex-row gap-3"><a href="${primaryHref}" class="inline-flex items-center justify-center gap-2 rounded-full border-2 border-orange-400 bg-white/70 text-gray-900 font-semibold px-5 py-3" style="color:#1f2937;text-decoration:none;"><span class="flex items-center justify-center w-7 h-7 rounded-md bg-orange-500 text-white shrink-0">${ICON_SVGS.building2}</span>${primaryLabel}</a><a href="${secondaryHref}" class="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-600 to-rose-600 text-white font-semibold px-5 py-3 shadow-md" style="color:#fff;text-decoration:none;"><span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/20 shrink-0">${ICON_SVGS.headset}</span>${secondaryLabel}</a></div></div></div></div>`;
          }

          case "delimiter":
            return `<hr class="my-8 border-t-2 border-gray-300 dark:border-gray-600" />`;

          case "warning":
            return `<div class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 my-4"><p class="text-sm text-yellow-700 dark:text-yellow-300">${
              block.data.title
                ? `<strong>${block.data.title}</strong><br/>`
                : ""
            }${block.data.message || text}</p></div>`;

          default:
            // Handle unknown block types gracefully
            console.warn("Unknown block type:", block.type);
            return text
              ? `<p class="text-base leading-relaxed text-gray-900 dark:text-gray-100">${text}</p>`
              : "";
        }
      })
      .filter((html) => html !== "") // Remove empty strings
      .join("");
  }, []);

  // Initialize Editor.js
  useEffect(() => {
    if (isInitialized.current || !editorContainerRef.current) return;

    const editor = new EditorJS({
      holder: editorContainerRef.current,
      tools: {
        header: {
          class: Header,
          inlineToolbar: true,
          config: {
            placeholder: "Enter header...",
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 2,
          },
          shortcut: "CMD+SHIFT+H",
        },
        list: {
          class: List,
          inlineToolbar: ["bold", "italic", "link"],
        },
        quote: {
          class: Quote,
          inlineToolbar: ["bold", "italic", "link"],
        },
        code: Code,
        table: {
          class: Table,
          inlineToolbar: true,
        },
        marker: { class: Marker, shortcut: "CMD+SHIFT+M" },
        inlineCode: { class: InlineCode, shortcut: "CMD+SHIFT+C" },
        embed: Embed,
        keyTakeaways: {
          class: KeyTakeawaysTool,
          inlineToolbar: false,
        },
        venueCta: {
          class: VenueCTATool,
          inlineToolbar: false,
        },
        altText: {
          class: AltTextTune,
        },
        image: {
          class: Image,
          tunes: ["altText"],
          config: {
            uploader: {
              // Uploads the file to S3 and uses the returned URL, instead of
              // embedding it as base64 — a single base64 image inlined into
              // the blog content can bloat that post's payload by tens of
              // megabytes, since an uploaded image the same size is normally
              // a few hundred KB.
              async uploadByFile(file) {
                try {
                  const formData = new FormData();
                  formData.append("image", file);
                  const res = await axios.post(
                    `${config.service}/masters/blogpost/upload-image/`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                  );
                  return { success: 1, file: { url: res.data.url } };
                } catch (err) {
                  console.error("Image upload failed:", err);
                  return { success: 0 };
                }
              },
              uploadByUrl(url) {
                return Promise.resolve({
                  success: 1,
                  file: { url },
                });
              },
            },
          },
        },
      },
      data: { time: 0, blocks: [], version: "2.26.0" },
      placeholder,
      minHeight: 400,
      logLevel: "ERROR",
    });

    editorInstance.current = editor;
    isInitialized.current = true;

    return () => {
      if (editorInstance.current?.destroy) {
        editorInstance.current.destroy();
      }
      editorInstance.current = null;
      isInitialized.current = false;
    };
  }, [placeholder]);
  // Update editor when data prop changes
  useEffect(() => {
    if (!editorInstance.current || !isInitialized.current) return;

    const newData = convertToBlocks(data);

    editorInstance.current.isReady
      .then(() => {
        if (editorInstance.current) {
          editorInstance.current.blocks.render(newData);
        }
      })
      .catch((err) => console.error("Editor.js render error:", err));
  }, [data, convertToBlocks]);

  // Reads the editor's current blocks and converts them to HTML. Exposed via
  // `ref` (below) so a parent form's own top-level Save/Update button can pull
  // the latest content directly at submit time — no dependency on the user
  // having clicked a separate save action inside the editor first.
  const saveAndGetHtml = useCallback(async () => {
    if (!editorInstance.current) return "";
    try {
      const savedData = await editorInstance.current.saver.save();
      const htmlValue = blocksToHtml(savedData.blocks || []);
      if (typeof onChange === "function") {
        onChange({
          target: {
            name,
            value: htmlValue,
            htmlValue,
          },
        });
      }
      return htmlValue;
    } catch (err) {
      console.error("Editor.js save error:", err);
      return "";
    }
  }, [onChange, name, blocksToHtml]);

  useImperativeHandle(ref, () => ({ save: saveAndGetHtml }), [saveAndGetHtml]);

  return (
    <div ref={wrapperRef} className="relative">
      <div
        ref={editorContainerRef}
        className="border border-gray-300 rounded-lg pt-14 px-3 pb-3 min-h-[400px] bg-white mb-3"
      />
    </div>
  );
});

export default EditorJsComponent;
